import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, getToken, setToken } from "@/api/client";

const AuthContext = createContext(null);

function extractToken() {
  const src = `${window.location.hash}&${window.location.search}`;
  const m = src.match(/[?#&]session_token=([^&#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const t = getToken();
      if (!t) {
        setUser(null);
        return;
      }
      const me = await api("/auth/me");
      setUser(me);
    } catch {
      setToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const tok = extractToken();
      if (tok) {
        setToken(tok);
        try {
          const url = new URL(window.location.href);
          url.hash = "";
          url.searchParams.delete("session_token");
          window.history.replaceState(window.history.state, "", url.toString());
        } catch (e) { console.debug("URL cleanup failed", e); }
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Start Google OAuth on the SAME origin the user is on (Emergent routes /api/* on every
    // app domain — preview + custom domain — to the backend). This keeps the backend host,
    // the redirect allowlist and Google's redirect_uri all aligned, so it works on the custom
    // domain (vhrpconnect.com) exactly as on preview.
    const redirectUrl = window.location.origin + "/";
    window.location.href = `${window.location.origin}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (e) { console.error("Logout request failed", e); }
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginWithGoogle, logout, refresh }),
    [user, loading, loginWithGoogle, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
